import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import {
  LOCATION_TYPES,
  type NewLocationDraft,
} from './roadsImporter.constants';
import {
  sanitizeIntegerInput,
  sanitizeNameInput,
} from './roadsImporter.helpers';

interface AddLocationDialogProps {
  open: boolean;
  value: NewLocationDraft;
  existingNames: string[];
  onChange: (next: NewLocationDraft) => void;
  onClose: () => void;
  onContinue: (draft: NewLocationDraft) => void;
}

export function AddLocationDialog({
  open,
  value,
  existingNames,
  onChange,
  onClose,
  onContinue,
}: AddLocationDialogProps): JSX.Element {
  const { t } = useTranslation();

  const trimmedName = value.name?.trim() ?? '';
  const isDuplicate =
    trimmedName.length > 0 &&
    existingNames.some(
      (name) => name.toLowerCase() === trimmedName.toLowerCase()
    );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>
        {t('roads_importer_add_title')}
      </DialogTitle>
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}
      >
        <TextField
          autoFocus
          size="small"
          label={t('roads_importer_field_name')}
          value={value.name}
          onChange={(e) => {
            onChange({ ...value, name: sanitizeNameInput(e.target.value) });
          }}
          error={isDuplicate}
          helperText={isDuplicate ? t('roads_importer_name_taken') : undefined}
          sx={{ mt: 1 }}
        />
        <TextField
          select
          size="small"
          label={t('roads_importer_field_type')}
          value={value.locationType}
          onChange={(e) => {
            onChange({ ...value, locationType: e.target.value });
          }}
        >
          {LOCATION_TYPES.map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          type="number"
          label={t('roads_importer_field_capacity')}
          value={value.trucksCapacity}
          onChange={(e) => {
            onChange({
              ...value,
              trucksCapacity: sanitizeIntegerInput(e.target.value),
            });
          }}
        />
        <TextField
          size="small"
          type="number"
          label={t('roads_importer_field_elevation')}
          value={value.elevation}
          onChange={(e) => {
            onChange({ ...value, elevation: e.target.value });
          }}
        />
        <TextField
          size="small"
          type="number"
          label={t('roads_importer_field_location_minutes')}
          value={value.minutes}
          onChange={(e) => {
            onChange({
              ...value,
              minutes: sanitizeIntegerInput(e.target.value),
            });
          }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={onClose}
          sx={{ borderRadius: '8px', fontWeight: 600 }}
        >
          {t('roads_importer_btn_cancel')}
        </Button>
        <Button
          variant="contained"
          size="small"
          disabled={!trimmedName || isDuplicate}
          onClick={() => {
            onContinue({ ...value, name: trimmedName });
          }}
          sx={{ borderRadius: '8px', fontWeight: 600 }}
        >
          {t('roads_importer_btn_continue')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
